import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authErrorStorage, consumeTokenFromUrl, login, logout, tokenStorage } from './lib/auth';
import { MOCK, onUnauthorized } from './lib/api';
import { useBoard, useConfig, useMe, useOffices } from './lib/queries';
import { BoardView } from './views/BoardView';
import { RulesSheet } from './components/RulesSheet';
import { AppHeader } from './components/AppHeader';
import { LoginView } from './views/LoginView';
import { OfficeSelectionView } from './views/OfficeSelectionView';
import { NoAccessView } from './views/NoAccessView';
import type { DashboardLayout } from './lib/layout';

// El admin (con los gráficos de recharts) se carga solo al abrirlo: no pesa en la pantalla de la reunión
const AdminView = lazy(() => import('./views/AdminView').then((m) => ({ default: m.AdminView })));

const OFFICE_KEY = 'odd:office';
const read = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const write = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* sin almacenamiento */
  }
};

consumeTokenFromUrl();

const officeFromUrl = () => new URLSearchParams(window.location.search).get('office');
type View = 'board' | 'admin';
const viewFromUrl = (): View =>
  new URLSearchParams(window.location.search).get('view') === 'admin' ? 'admin' : 'board';

function setViewInUrl(v: View) {
  const url = new URL(window.location.href);
  if (v === 'admin') url.searchParams.set('view', 'admin');
  else url.searchParams.delete('view');
  window.history.pushState(null, '', url);
}

/** Refleja la oficina en `?office=` para que recargar o compartir el enlace mantenga la oficina. */
function setOfficeInUrl(id: string, mode: 'push' | 'replace') {
  const url = new URL(window.location.href);
  if (url.searchParams.get('office') === id) return;
  url.searchParams.set('office', id);
  if (mode === 'push') window.history.pushState(null, '', url);
  else window.history.replaceState(null, '', url);
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="grid min-h-[calc(100vh-4rem)] place-items-center px-4">
      <div className="grid max-w-md justify-items-center gap-4 text-center">{children}</div>
    </div>
  );
}

const AUTH_REASON_TEXT: Record<string, string> = {
  signature:
    'La firma de la sesión que envía Eleva no es válida para este panel. No es un problema de tu usuario: avisa al equipo técnico.',
  expired: 'Tu sesión ha caducado. Vuelve a entrar.',
  domain: 'Tu usuario no pertenece al dominio de este panel.',
  malformed: 'La sesión recibida no es válida.',
  no_agent: 'La sesión recibida no identifica a ningún usuario.',
};

/** Por qué se rechazó el último acceso (401), con detalles técnicos para depurar */
function AuthErrorNotice() {
  const e = authErrorStorage.get();
  if (!e || e.reason === 'missing') return null;
  return (
    <div
      role="alert"
      className="mt-2 grid max-w-lg gap-2 rounded-2xl border border-bad/40 bg-bad-soft p-4 text-left text-sm"
    >
      <b className="text-bad">No se pudo validar tu acceso</b>
      <p className="text-ink-2">{AUTH_REASON_TEXT[e.reason] ?? e.message}</p>
      <details className="text-xs text-ink-3">
        <summary className="cursor-pointer select-none">Detalles técnicos</summary>
        <pre className="mt-2 overflow-x-auto rounded-lg bg-card p-2 whitespace-pre-wrap">
          {JSON.stringify(
            { motivo: e.reason, mensaje: e.message, ruta: e.path, cuando: e.at, token: e.claims },
            null,
            2,
          )}
        </pre>
      </details>
    </div>
  );
}

export function App({ layout = 'classic' }: { layout?: DashboardLayout } = {}) {
  const qc = useQueryClient();
  const [hasToken, setHasToken] = useState(() => MOCK || !!tokenStorage.get());
  const [rulesOpen, setRulesOpen] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const [view, setViewState] = useState<View>(viewFromUrl);
  const setView = (v: View) => {
    setViewState(v);
    setViewInUrl(v);
    window.scrollTo(0, 0);
  };
  const [officeId, setOfficeIdState] = useState<string | null>(() => officeFromUrl() ?? read(OFFICE_KEY));

  useEffect(
    () =>
      onUnauthorized(() => {
        setHasToken(false);
        qc.clear();
      }),
    [qc],
  );

  const config = useConfig();
  const me = useMe(hasToken);
  const offices = useOffices(hasToken);
  const list = offices.data ?? [];
  const validOffice = list.find((o) => o.id === officeId)?.id ?? (list.length === 1 ? list[0]!.id : null);
  const board = useBoard(hasToken ? validOffice : null, runId);

  // Atrás/adelante del navegador entre oficinas
  useEffect(() => {
    const onPop = () => {
      setViewState(viewFromUrl());
      const id = officeFromUrl();
      setRunId(null);
      setOfficeIdState(id);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Cuando la oficina se resuelve sola (única visible o la recordada), también va a la URL
  useEffect(() => {
    if (validOffice) {
      setOfficeInUrl(validOffice, 'replace');
      write(OFFICE_KEY, validOffice);
    }
  }, [validOffice]);

  const setOfficeId = (id: string) => {
    setRunId(null);
    setOfficeIdState(id);
    write(OFFICE_KEY, id);
    setOfficeInUrl(id, 'push');
  };

  if (!hasToken)
    return (
      <LoginView
        isLoading={!config.data && !config.isError}
        isUnavailable={!config.data && config.isError}
        onLogin={() => config.data && login(config.data.elevauthUrl, config.data.domain)}
        onRetry={() => void config.refetch()}
        authNotice={<AuthErrorNotice />}
      />
    );

  const onLogout = config.data && !MOCK ? () => logout(config.data!.elevauthUrl) : undefined;

  // Sin un rol con acceso (direcciones y jefes de equipo): pantalla propia, sin pedir las oficinas
  if (me.data && !me.data.access.allowed)
    return (
      <NoAccessView
        email={me.data.email}
        roles={me.data.access.roles}
        erpUrl={config.data?.erpUrl}
        onLogout={onLogout}
      />
    );

  if (!validOffice)
    return (
      <OfficeSelectionView
        offices={list}
        email={me.data?.email}
        isLoading={me.isLoading || offices.isLoading}
        error={me.isError || offices.isError ? (me.error ?? offices.error)?.message : undefined}
        onSelect={setOfficeId}
        onRetry={() => {
          if (me.isError) void me.refetch();
          if (offices.isError) void offices.refetch();
        }}
        onLogout={onLogout}
      />
    );

  let body: ReactNode;
  if (me.isLoading || offices.isLoading)
    body = (
      <Centered>
        <p className="text-ink-3">Cargando…</p>
      </Centered>
    );
  else if (me.isError || offices.isError)
    body = (
      <Centered>
        <h1 className="text-2xl font-extrabold">No se pudo cargar</h1>
        <p className="text-ink-3">{(me.error ?? offices.error)?.message}</p>
      </Centered>
    );
  else if (board.isLoading || !me.data)
    body = (
      <Centered>
        <p className="text-ink-3">Cargando la foto de la oficina…</p>
      </Centered>
    );
  else if (board.isError || !board.data)
    body = (
      <Centered>
        <h1 className="text-2xl font-extrabold">No se pudo cargar la oficina</h1>
        <p className="text-ink-3">{board.error?.message}</p>
      </Centered>
    );
  else if (view === 'admin' && me.data.canAdmin)
    body = (
      <Suspense
        fallback={
          <Centered>
            <p className="text-ink-3">Cargando la administración…</p>
          </Centered>
        }
      >
        <AdminView key={validOffice} board={board.data} officeId={validOffice} />
      </Suspense>
    );
  else
    body = (
      <BoardView
        key={validOffice}
        board={board.data}
        me={me.data}
        officeId={validOffice}
        onSelectRun={setRunId}
        loading={board.isFetching}
        layout={layout}
      />
    );

  return (
    <>
      <AppHeader
        offices={list}
        officeId={validOffice}
        setOfficeId={setOfficeId}
        onHome={() => setView('board')}
        email={me.data?.email}
        onLogout={onLogout}
        onRules={board.data ? () => setRulesOpen(true) : undefined}
        view={view}
        onView={me.data?.canAdmin ? setView : undefined}
      />
      {body}
      {board.data && me.data && validOffice && (
        <RulesSheet
          isOpen={rulesOpen}
          onClose={() => setRulesOpen(false)}
          rules={board.data.rules}
          scoring={board.data.scoring}
          onOpenAdmin={
            me.data.canAdmin
              ? () => {
                  setRulesOpen(false);
                  setView('admin');
                }
              : undefined
          }
        />
      )}
    </>
  );
}
